import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogContent, DialogTitle, Divider, IconButton, Menu, MenuItem,
  ListItemIcon, ListItemText, Stack, Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import { DischargePatient, PHASE_ORDER } from '~/types/discharge';
import { useDischargeContext } from '~/hooks/discharge/useDischargeContext';
import { buildReferralPacket } from './buildReferralPacket';
import DischargeReferralPacketViewer from './DischargeReferralPacketViewer';
import { getSelectedProviderName } from './buildPlaceMatches';

const TINT = {
  bg: '#e0f7f4',
  border: '#9de4da',
  text: '#005a4e',
  btnBg: '#00bda1',
  btnHover: '#009e87',
};

interface Props {
  patient: DischargePatient;
}

const ReferSection: React.FC<Props> = ({ patient }) => {
  const { setPhase, setReferralSent, setReferralResponse, setSelectedProvider, setDeclinedProvider, setNoResponseProvider, sendToIntake } = useDischargeContext();
  const [packetOpen, setPacketOpen] = useState(false);
  const [demoAnchor, setDemoAnchor] = useState<HTMLElement | null>(null);
  const packet = useMemo(() => buildReferralPacket(patient), [patient]);

  const placeNotComplete = PHASE_ORDER.indexOf(patient.phase) < PHASE_ORDER.indexOf('refer');
  const referComplete = PHASE_ORDER.indexOf(patient.phase) > PHASE_ORDER.indexOf('refer');
  const referralSent = !!patient.referralSentAt || referComplete;
  const noResponse = patient.referralResponse === 'no_response' && !referComplete;
  const declined = patient.referralResponse === 'declined' && !referComplete;
  const providerName = getSelectedProviderName(patient) ?? 'provider';

  if (placeNotComplete) {
    return (
      <Typography variant="body2" color="text.secondary">
        Complete the Place phase to send the referral.
      </Typography>
    );
  }

  const sendReferral = () => {
    setReferralSent(patient.id, new Date().toISOString());
    setReferralResponse(patient.id, 'awaiting');
  };

  const simulateAccepted = () => {
    setDemoAnchor(null);
    setReferralResponse(patient.id, undefined);
    setPhase(patient.id, 'monitor');
    // If the accepting provider opts in to the intake treatment (a
    // "Metropolitan …" provider that resolves to a configured service line),
    // this also drops the patient into the Intake queue. The queue derives
    // eligibility via resolveIntakeServiceLine, so non-eligible providers set
    // the flag harmlessly but never surface there.
    sendToIntake(patient.id);
  };

  const simulateDeclined = () => {
    setDemoAnchor(null);
    const name = patient.selectedProviderName ?? getSelectedProviderName(patient);
    if (name) setDeclinedProvider(patient.id, name);
    setReferralResponse(patient.id, 'declined');
  };

  const changePlace = () => {
    setReferralSent(patient.id, undefined);
    setReferralResponse(patient.id, undefined);
    setSelectedProvider(patient.id, undefined);
    setPhase(patient.id, 'place');
  };

  const simulateNoResponse = () => {
    setDemoAnchor(null);
    const name = patient.selectedProviderName ?? getSelectedProviderName(patient);
    if (name) setNoResponseProvider(patient.id, name);
    setReferralResponse(patient.id, 'no_response');
  };

  const openDemo = (e: React.MouseEvent<HTMLElement>) => setDemoAnchor(e.currentTarget);

  // Common styling for the clickable response box.
  const responseBoxSx = (severity: 'info' | 'warning') => {
    const hover =
      severity === 'info' ? 'rgba(2,136,209,0.08)' :
      'rgba(237,108,2,0.08)';
    return {
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
      '&:hover': { bgcolor: hover },
    };
  };

  return (
    <Box>
      {/* Send-referral banner (only before the referral is sent) */}
      {!referralSent && (
        <Box
          sx={{
            border: '1px solid', borderColor: TINT.border, bgcolor: TINT.bg,
            borderRadius: 2, px: 3, py: 2, mb: 2,
          }}
        >
          <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap" useFlexGap>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: TINT.text, mb: 0.25 }}>
                Send referral to {providerName}
              </Typography>
              <Typography variant="body2" sx={{ color: TINT.text, opacity: 0.8, fontSize: '0.82rem' }}>
                Provider confirmed and referral packet assembled. Review the packet, then send.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexShrink={0} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined" size="small"
                startIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
                onClick={() => setPacketOpen(true)}
                sx={{
                  fontSize: '0.78rem', fontWeight: 600,
                  color: TINT.text, borderColor: TINT.border,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', borderColor: TINT.text },
                }}
              >
                View Referral Packet
              </Button>
              <Button
                variant="contained" size="small"
                endIcon={<ArrowForwardIcon sx={{ fontSize: 15 }} />}
                onClick={sendReferral}
                sx={{
                  bgcolor: TINT.btnBg,
                  '&:hover': { bgcolor: TINT.btnHover },
                  fontWeight: 700, fontSize: '0.82rem',
                }}
              >
                Send Referral
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {/* Awaiting / No-response / Declined */}
      {referralSent && !referComplete && (
        <Alert
          severity={declined || noResponse ? 'warning' : 'info'}
          sx={{
            mb: 2,
            ...responseBoxSx(declined || noResponse ? 'warning' : 'info'),
          }}
          onClick={openDemo}
        >
          <Typography variant="body2" fontWeight={600}>
            {declined
              ? `Referral Declined by ${providerName}`
              : `Referral Sent · Awaiting Response from ${providerName}`}
          </Typography>
          {noResponse && (
            <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
              No response in over 3 hours
            </Typography>
          )}
          {patient.referralSentAt && (
            <Typography variant="caption">
              Sent {new Date(patient.referralSentAt).toLocaleString()}
            </Typography>
          )}
          {(noResponse || declined) && (
            <Box sx={{ mt: 1.5 }}>
              <Button
                size="small"
                variant="contained"
                color="warning"
                onClick={(e) => { e.stopPropagation(); changePlace(); }}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Change Place
              </Button>
            </Box>
          )}
        </Alert>
      )}

      {/* Accepted */}
      {referComplete && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            Referral Accepted by {providerName}
          </Typography>
          {patient.referralSentAt && (
            <Typography variant="caption">
              Sent {new Date(patient.referralSentAt).toLocaleString()}
            </Typography>
          )}
        </Alert>
      )}

      {(referralSent || referComplete) && (
        <Button
          size="small" variant="outlined"
          startIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
          onClick={() => setPacketOpen(true)}
        >
          View Referral Packet
        </Button>
      )}

      {/* Demo menu — appears when the user clicks the Awaiting / Negotiating box */}
      <Menu
        anchorEl={demoAnchor}
        open={!!demoAnchor}
        onClose={() => setDemoAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { minWidth: 220 } }}
      >
        <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.8 }}>
            Demo
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={simulateAccepted}>
          <ListItemIcon><ThumbUpIcon fontSize="small" sx={{ color: '#006e60' }} /></ListItemIcon>
          <ListItemText>Simulate: Accepted</ListItemText>
        </MenuItem>
        <MenuItem onClick={simulateDeclined}>
          <ListItemIcon><ThumbDownIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Simulate: Declined</ListItemText>
        </MenuItem>
        <MenuItem onClick={simulateNoResponse}>
          <ListItemIcon><AccessTimeIcon fontSize="small" sx={{ color: '#bf360c' }} /></ListItemIcon>
          <ListItemText>Simulate: No response</ListItemText>
        </MenuItem>
      </Menu>

      {/* Referral packet dialog */}
      <Dialog open={packetOpen} onClose={() => setPacketOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          Referral Packet
          <IconButton onClick={() => setPacketOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <DischargeReferralPacketViewer packet={packet} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReferSection;
